import React, { useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import NextButton from "./components/NextButton";
import PrevButton from "./components/PreviousButton";
import Welcome from "./components/welcome";
import CompanyName from "./components/company_name";
import ClientName from "./components/client_name";
import ClientEmail from "./components/client_email";
import ClientPhone from "./components/client_phone";
import CompanyOverview from "./components/company_overview";
import Audience from "./components/audience";
import Competitors from "./components/competitors";
import QuestionNewWebsite from "./components/question_new_website";
import ListWebsiteYouLike from "./components/list_websites";
import Functionality from "./components/functionality";
import CheckBoxExpectations from "./components/checkbox_expectations";
import LookAndFeelPreferences from "./components/lookandfeel";
import QuestionSelling from "./components/question_selling";
import ExistingPhotography from "./components/existingphotography";
import ContentReadiness from "./components/contentreadiness";
import ContentUpdateFrequency from "./components/contentupdatefrequency";
import WebsiteLaunchTimeline from "./components/websitelaunchtimeline";
import AdditionalInfo from "./components/additionalinfo";
import ReferralSource from "./components/referralsource";

// Fields grouped into a handful of logical sections instead of one-per-screen.
// Short fields sit two-per-row; `full: true` makes a field span the whole width.
const steps = [
  {
    kicker: "Welcome",
    title: null,
    intro: true,
    fields: [{ C: Welcome, full: true }],
  },
  {
    kicker: "Section 01",
    title: "About you & your business",
    fields: [
      { C: ClientName },
      { C: ClientEmail },
      { C: ClientPhone },
      { C: CompanyName },
      { C: CompanyOverview, full: true },
      { C: Audience, full: true },
      { C: Competitors, full: true },
    ],
  },
  {
    kicker: "Section 02",
    title: "Your website project",
    fields: [
      { C: QuestionNewWebsite },
      { C: QuestionSelling },
      { C: CheckBoxExpectations, full: true },
      { C: Functionality, full: true },
      { C: ListWebsiteYouLike, full: true },
    ],
  },
  {
    kicker: "Section 03",
    title: "Design & content",
    fields: [
      { C: LookAndFeelPreferences, full: true },
      { C: ContentReadiness, full: true },
      { C: ExistingPhotography },
      { C: ContentUpdateFrequency },
    ],
  },
  {
    kicker: "Section 04",
    title: "Timeline & final details",
    fields: [
      { C: WebsiteLaunchTimeline, full: true },
      { C: AdditionalInfo, full: true },
      { C: ReferralSource, full: true },
    ],
  },
];

export default function RequirementForm() {
  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({ mode: "onBlur", criteriaMode: "all" });
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const onSubmit = async (data) => {
    console.log(data);
    setIsLoading(true);
    try {
      const requestData = { data: data };
      const response = await axios.post(
        "https://ub0967ih47.execute-api.ap-southeast-2.amazonaws.com/default/wdc",
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Check if the request was successful
      if (response.status === 200) {
        setIsSubmitted(true);
      } else {
        // Handle non-successful responses
        console.error("Response not OK:", response);
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Sorry an error occurred please contact Admin");
    } finally {
      setIsLoading(false);
    }
  };

  const nextStep = async () => {
    const isCurrentStepValid = await trigger();
    if (isCurrentStepValid) {
      setCurrentStep((prevStep) => prevStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const prevStep = () => {
    setCurrentStep((prevStep) => prevStep - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const section = steps[currentStep];
  const isLast = currentStep === steps.length - 1;
  const isIntro = !!section.intro;
  const progress = Math.round(((currentStep + 1) / steps.length) * 100);

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-2xl">
        <div className="section-card flex flex-col items-center gap-5 py-16 text-center">
          <span className="relative flex h-12 w-12 items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-wdc-orange/20 pulse-ring" />
            <svg
              aria-hidden="true"
              role="status"
              className="h-9 w-9 animate-spin text-wdc-orange"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
                opacity="0.2"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentColor"
              />
            </svg>
          </span>
          <p className="text-lg font-semibold text-wdc-black">
            Sending your brief…
          </p>
          <p className="max-w-sm text-sm text-wdc-charcoal/70">
            Generating your project document and emailing a copy your way. This
            only takes a moment.
          </p>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="mx-auto w-full max-w-2xl">
        <div className="section-card fade-in-up flex flex-col items-center gap-5 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-wdc-orange/15 text-wdc-orange-deep">
            <svg
              className="h-7 w-7"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
          <h2 className="text-3xl font-bold tracking-tight text-wdc-black">
            Thank you!
          </h2>
          <p className="max-w-md text-base leading-relaxed text-wdc-charcoal/80">
            Your project brief has been received and a copy has been emailed to
            you. Our team will review the details and get back to you shortly
            with the next steps.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="section-card">
          {/* Progress */}
          <div className="mb-8">
            <div className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.16em] text-wdc-charcoal/60">
              <span>Project Brief</span>
              <span>
                Step {currentStep + 1} of {steps.length}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-wdc-line">
              <div
                className="h-full rounded-full bg-gradient-to-r from-wdc-orange to-wdc-orange-deep transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {!isIntro && (
            <header className="mb-7">
              <div className="wdc-section-kicker">{section.kicker}</div>
              <h2 className="wdc-section-title">{section.title}</h2>
            </header>
          )}

          <div
            key={currentStep}
            className="fade-in-up grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2"
          >
            {section.fields.map(({ C: Field, full }, i) => (
              <div key={i} className={full ? "sm:col-span-2" : ""}>
                <Field register={register} errors={errors} />
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-row justify-center gap-4">
            {currentStep > 0 && (
              <PrevButton onClick={prevStep} text="Go Back" />
            )}
            {!isLast && (
              <NextButton
                onClick={nextStep}
                text={currentStep === 0 ? "Get Started" : "Next"}
              />
            )}
            {isLast && (
              <button type="submit" className="wdc-btn-primary">
                Submit brief
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
